import React from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import colors from '../../themes/main-colors';
import { useTranslation } from '../../hooks/useTranslation';
import { navigate } from 'gatsby';
import ROUTES from '../../routes';
import { useLocation } from '@reach/router';
import {
  AuthDriverList,
  ChargePointAction,
  ManagePerRelationshipResponse,
} from '../../__generated__/pge-types';
import { useIsMobile } from '../../util/style-utils';
import ReactMarkdown from 'react-markdown/with-html';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      margin: '0px 0px 15px 0px',
      textAlign: 'center',
      fontFamily: 'Forma-DJR-Display',
    },
    subHeading: {
      marginBottom: '0.5rem',
      [theme.breakpoints.down('sm')]: {
        marginLeft: '1.3rem',
      },
    },
    deleteSubHeading: {
      marginTop: '1rem',
      marginBottom: '0.5rem',
      [theme.breakpoints.down('sm')]: {
        marginLeft: '1.3rem',
      },
    },
    back: {
      margin: '45px 40px 10px',
      width: '100px',
      height: '40px',
      [theme.breakpoints.up('sm')]: {
        marginRight: '15px',
      },
    },
    text: {
      paddingRight: '10px',
      margin: '0px 0px 0px 20px',
      fontWeight: 600,
      fontFamily: 'Untitled-Sans',
      float: 'left',
      [theme.breakpoints.down('sm')]: {
        margin: '0px 0px 0px 20px',
      },
    },
    textValue: {
      fontFamily: 'Untitled-Sans',
      overflow: 'auto',
      textTransform: 'capitalize',
    },
    ShellEmail: {
      fontFamily: 'Untitled-Sans',
      overflow: 'auto',
      textTransform: 'lowercase',
    },
    divider: {
      marginTop: '5px',
      marginBottom: '5px',
    },
    addedDrivers: {
      padding: '0.2rem 1rem 1rem 1rem',
      background: colors.white,
      borderRadius: '5px',
      boxShadow: '1px 2px 2px #00000029',
    },
    updatedDrivers: {
      padding: '1rem',
      background: colors.white,
      borderRadius: '10px',
      marginTop: '15px',
      boxShadow: '1px 2px 2px #00000029',
    },
    marginStyles: {
      marginBottom: '2px',
    },
    button: {
      borderRadius: '5px',
      padding: '8px 45px',
      fontWeight: 'bold',
      fontSize: '18px',
      fontFamily: 'Untitled-Sans',
      display: 'block',
      '&:hover': {
        boxShadow: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        padding: '8px 40px',
        fontSize: '14px',
      },
    },
  }),
);

type Props = {
  path?: string;
};

type StateObject = {
  response: ManagePerRelationshipResponse;
  isDeleted: boolean;
};
type DriverDetails = AuthDriverList & {
  action: string;
};

export default function ChargepointSubmitted(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { response, isDeleted } = location.state as StateObject;
  const driverData: any = response;
  const addedDrivers =
    driverData &&
    driverData.filter(
      (driverDetails: DriverDetails) =>
        driverDetails.action === ChargePointAction.Add,
    );
  const updatedDrivers =
    driverData &&
    driverData.filter(
      (driverDetails: DriverDetails) =>
        driverDetails.action === ChargePointAction.Update,
    );

  const addActionData =
    driverData &&
    driverData.filter(
      (driverDetails: DriverDetails) =>
        driverDetails.action === ChargePointAction.Add,
    ).length === driverData.length;
  const updateActionData =
    driverData &&
    driverData.filter(
      (driverDetails: DriverDetails) =>
        driverDetails.action === ChargePointAction.Update,
    ).length === driverData.length;
  const addAndUpdateActionData =
    driverData &&
    driverData.find(
      (driverDetails: DriverDetails) =>
        driverDetails.action === ChargePointAction.Add,
    ) &&
    driverData.find(
      (driverDetails: DriverDetails) =>
        driverDetails.action === ChargePointAction.Update,
    )
      ? true
      : false;

  const headingText = addActionData
    ? 'CHARGEPOINT_SUBMITTED_HEADING'
    : updateActionData
    ? 'OPCONNECT_UPDATED_HEADING'
    : 'ADD_UPDATE_USER_AND_OPCONNECTEMAIL';

  return (
    <Grid container style={{ justifyContent: 'center' }}>
      <Paper
        style={{
          width: '700px',
          padding: '20px 20px 5px 20px',
          margin: isMobile ? '20px' : '40px',
          background: colors.riverBlue,
        }}
      >
        <Box>
          <Typography variant="h2" className={classes.heading}>
            {isDeleted ? t('CHARGEPOINT_DELETED_HEADING') : t(headingText)}{' '}
          </Typography>
          {addedDrivers.length > 0 && (
            <div className={classes.addedDrivers}>
              <Typography variant="h3" className={classes.subHeading}>
                <ReactMarkdown
                  source={t('CHARGEPOINT_SUBMITTED_SUB_HEADING_TEXT', {
                    CHARGEPOINT_LINK: 'https://drivers.chargepoint.com',
                  })}
                  escapeHtml={false}
                />
              </Typography>
              {addedDrivers.map((addedDriver: any) => {
                return (
                  <>
                    <Grid container>
                      <Grid item xs={12}>
                        <div className={classes.marginStyles}>
                          <Typography className={classes.text}>
                            {t('CHARGEPOINT_DRIVER')}:
                          </Typography>
                          <Typography className={classes.textValue}>
                            {addedDriver?.personName}
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12} className={classes.marginStyles}>
                        <Typography className={classes.text}>
                          {t('CHARGEPOINT_ACCESS_CODE_TEXT')}
                        </Typography>
                        <Typography className={classes.textValue}>
                          {addedDriver?.accessCode}
                        </Typography>
                      </Grid>
                      {addedDriver?.opConnectEmail && (
                        <Grid item xs={12}>
                          <Typography className={classes.text}>
                            {t('EMAIL')}:
                          </Typography>
                          <Typography className={classes.ShellEmail}>
                            {addedDriver?.opConnectEmail}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    <Divider className={classes.divider} />
                  </>
                );
              })}
            </div>
          )}

          {updatedDrivers.length > 0 && (
            <div className={classes.updatedDrivers}>
              <Typography variant="h3" className={classes.subHeading}>
                {t('OPCONNECTEMAIL_UPDATED_SUBHEADING')}
              </Typography>
              {updatedDrivers.map((updatedDriver: any) => {
                return (
                  <>
                    <Grid container>
                      <Grid item xs={12}>
                        <div className={classes.marginStyles}>
                          <Typography className={classes.text}>
                            {t('CHARGEPOINT_DRIVER')}:
                          </Typography>
                          <Typography className={classes.textValue}>
                            {updatedDriver?.personName}
                          </Typography>
                        </div>
                      </Grid>
                      {!(
                        updatedDriver?.opConnectEmail === null &&
                        updatedDriver?.opConnectEmail === undefined
                      ) && (
                        <Grid item xs={12}>
                          <Typography className={classes.text}>
                            {t('EMAIL')}:
                          </Typography>
                          <Typography className={classes.ShellEmail}>
                            {updatedDriver?.opConnectEmail}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    <Divider className={classes.divider} />
                  </>
                );
              })}
            </div>
          )}

          {isDeleted && (
            <div className={classes.addedDrivers}>
              <Typography variant="h3" className={classes.deleteSubHeading}>
                {' '}
                {t('CHARGEPOINT_DELETED_SUB_HEADING')}
              </Typography>

              <>
                <Grid container>
                  <Grid item xs={12}>
                    <div className={classes.marginStyles}>
                      <Typography className={classes.text}>
                        {t('CHARGEPOINT_DRIVER')}:
                      </Typography>
                      <Typography className={classes.textValue}>
                        {driverData[0]?.personName}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
              </>
            </div>
          )}
        </Box>
        <Grid
          container
          style={{ justifyContent: 'center', margin: '15px 0px' }}
        >
          <Button
            type={'submit'}
            variant={'contained'}
            color={'primary'}
            className={classes.button}
            css={{
              backgroundColor: colors.buttonHoverPrimary,
              border: `1px solid ${colors.buttonHoverPrimary}`,
              color: colors.white,
              width: isMobile ? '85%' : '',
              borderRadius: isMobile ? '30px' : '',
            }}
            onClick={() => navigate(ROUTES.CHARGEPOINT)}
          >
            {t('BACK')}
          </Button>
        </Grid>
      </Paper>
    </Grid>
  );
}
